import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, ShieldCheck, Copy, Check, X, Clock, MailCheck, AlertCircle, RefreshCw, Info, DollarSign } from "lucide-react";
import api from "@/lib/api";

/**
 * BeneficiaryInvitesCard
 * ------------------------
 * Shown on the Case Workspace to case owners, assigned attorneys/form-fillers,
 * and admins. Lets the sender email a secure review link to the beneficiary
 * and manage outstanding invites (revoke, copy fallback URL, view audit log).
 */
export default function BeneficiaryInvitesCard({ caseId, caseData }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permitted, setPermitted] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/beneficiary-invites`);
      setInvites(data.invites || []);
      setPermitted(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setPermitted(false);
      } else {
        console.warn("[BeneficiaryInvitesCard] load failed:", err);
      }
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  if (!permitted) return null; // Silently hide if the viewer isn't allowed.

  const activeInvite = invites.find((i) => !i.revoked && !i.submitted_at);

  return (
    <section className="rounded-2xl border p-6" style={{borderColor:"var(--border)",background:"var(--bg-card)"}}
             data-testid="beneficiary-invites-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium mb-2"
               style={{background:"#FDF8F0",color:"#8A6B4F"}}>
            <ShieldCheck size={12} /> Beneficiary Review
          </div>
          <h3 className="font-display text-xl font-bold" style={{color:"var(--text-primary)"}}>
            Invite the beneficiary to review their own data
          </h3>
          <p className="text-sm mt-1" style={{color:"var(--text-secondary)"}}>
            Send a secure, two-factor link so the beneficiary can review, correct, and complete their own information — safely.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shrink-0"
          style={{background:"#C85A32"}}
          data-testid="beneficiary-invite-open-modal"
        >
          <Send size={14} /> Send secure link
        </button>
      </div>

      {loading ? (
        <p className="text-sm mt-2" style={{color:"var(--text-secondary)"}}>Loading invites…</p>
      ) : invites.length === 0 ? (
        <div className="mt-3 text-sm rounded-lg p-3" style={{background:"var(--bg-input)",color:"var(--text-secondary)"}}>
          No invites sent yet. Click <strong>Send secure link</strong> to invite the beneficiary.
        </div>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="beneficiary-invites-list">
          {invites.map((inv) => (
            <InviteRow key={inv.id} caseId={caseId} invite={inv} onChange={load} />
          ))}
        </ul>
      )}

      <BeneficiaryNoteToPetitionerBanner caseData={caseData} />

      {showModal && (
        <InviteModal
          caseId={caseId}
          caseData={caseData}
          hasActive={!!activeInvite}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}
    </section>
  );
}

function InviteRow({ caseId, invite, onChange }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const status = invite.submitted_at
    ? { label: "Completed", color: "#4B6A4F", bg: "#EEF3EA" }
    : invite.revoked
      ? { label: "Revoked", color: "#8A6B4F", bg: "var(--bg-input)" }
      : invite.access_count > 0
        ? { label: "Opened — awaiting submission", color: "#8A6B4F", bg: "#FDF8F0" }
        : { label: "Sent — awaiting open", color: "#8A6B4F", bg: "#FDF8F0" };

  const uploadCount = Number(invite.docs_uploaded_count || 0);
  const fieldCount = Number(invite.changed_field_count || 0);

  const revoke = async () => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/beneficiary-invites/${invite.id}/revoke`);
      toast.success("Invite revoked.");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke.");
    } finally { setBusy(false); }
  };

  return (
    <li className="rounded-lg border p-3 text-sm"
        style={{borderColor:"var(--border)",background:"var(--bg-input)"}}
        data-testid={`beneficiary-invite-row-${invite.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate" style={{color:"var(--text-primary)"}}>
            {invite.beneficiary_email}
          </div>
          <div className="text-xs mt-1 flex items-center gap-3 flex-wrap" style={{color:"var(--text-secondary)"}}>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                  style={{background:status.bg,color:status.color}}>
              {invite.submitted_at ? <Check size={11}/> : invite.revoked ? <X size={11}/> : <Clock size={11}/>} {status.label}
            </span>
            <span>Sent {formatShort(invite.created_at)}</span>
            <span>Expires {formatShort(invite.expires_at)}</span>
            {invite.access_count > 0 && <span>Opened {invite.access_count}×</span>}
            {uploadCount > 0 && <span><strong>{uploadCount}</strong> doc{uploadCount === 1 ? "" : "s"} uploaded</span>}
            {fieldCount > 0 && <span><strong>{fieldCount}</strong> field{fieldCount === 1 ? "" : "s"} edited</span>}
            {invite.last_accessed_at && <span>Last seen {formatShort(invite.last_accessed_at)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!invite.revoked && !invite.submitted_at && (
            <button onClick={revoke} disabled={busy}
                    className="text-xs px-2 py-1 rounded border font-medium disabled:opacity-50"
                    style={{borderColor:"var(--border)",color:"var(--text-secondary)"}}
                    data-testid={`beneficiary-invite-revoke-${invite.id}`}>
              Revoke
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function InviteFormFields({ email, setEmail, dob, setDob, message, setMessage, busy, onClose, onSubmit }) {
  const inputStyle = { borderColor: "var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" };
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{color:"var(--text-primary)"}}>Beneficiary email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
               placeholder="beneficiary@example.com" required
               className="w-full rounded-lg border px-3 py-2 outline-none focus:ring-2"
               style={inputStyle}
               data-testid="beneficiary-invite-email" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{color:"var(--text-primary)"}}>
          Beneficiary date of birth
        </label>
        <input type="text" value={dob} onChange={(e) => setDob(e.target.value)}
               placeholder="YYYY-MM-DD" required
               className="w-full rounded-lg border px-3 py-2 outline-none focus:ring-2"
               style={inputStyle}
               data-testid="beneficiary-invite-dob" />
        <p className="text-[11px] mt-1" style={{color:"var(--text-secondary)"}}>
          The beneficiary will need to enter this DOB to unlock the link. Make sure it's correct.
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{color:"var(--text-primary)"}}>
          Personal message (optional)
        </label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                  rows={3} maxLength={500}
                  placeholder="Hi! Please double-check your address and passport info."
                  className="w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 text-sm"
                  style={inputStyle}
                  data-testid="beneficiary-invite-message" />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-medium border"
                style={{borderColor:"var(--border)",color:"var(--text-secondary)"}}>Cancel</button>
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{background:"#C85A32"}} data-testid="beneficiary-invite-send">
          <Send size={14}/> {busy ? "Sending…" : "Send secure link"}
        </button>
      </div>
    </form>
  );
}

function InviteModal({ caseId, caseData, hasActive, onClose, onCreated }) {
  const prefilledDob = (caseData?.data && caseData.data.beneficiary_dob) || "";
  const prefilledEmail = (caseData?.data && caseData.data.beneficiary_email) || "";

  const [email, setEmail] = useState(prefilledEmail);
  const [dob, setDob] = useState(prefilledDob);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !dob.trim()) {
      toast.error("Email and date of birth are required.");
      return;
    }
    if (hasActive && !result) {
      const proceed = window.confirm("There's already an active invite. Send another one anyway?");
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/beneficiary-invites`, {
        beneficiary_email: email.trim(),
        beneficiary_dob: dob.trim(),
        personal_message: message.trim() || undefined,
      });
      setResult(data);
      if (data.email_delivery?.delivered) {
        toast.success("Secure link emailed to the beneficiary.");
      } else {
        toast.info("Invite created — email delivery failed, share the link manually below.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
         style={{background:"rgba(20,16,12,0.6)"}} onClick={onClose}
         data-testid="beneficiary-invite-modal">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto"
           style={{borderColor:"var(--border)",background:"var(--bg-card)"}}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl font-bold" style={{color:"var(--text-primary)"}}>
              Send secure review link
            </h3>
            <p className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
              The beneficiary will get an email with a link. On first open, they'll need to enter their date of birth AND a 6-digit code emailed to them separately.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:opacity-70" style={{color:"var(--text-secondary)"}}
                  data-testid="beneficiary-invite-close">
            <X size={20} />
          </button>
        </div>

        {!result ? (
          <InviteFormFields
            email={email} setEmail={setEmail}
            dob={dob} setDob={setDob}
            message={message} setMessage={setMessage}
            busy={busy} onClose={onClose} onSubmit={submit}
          />
        ) : (
          <SentSummary result={result} onClose={onCreated} />
        )}
      </div>
    </div>
  );
}

function SentSummary({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const delivered = !!result.email_delivery?.delivered;
  const fallback = result.fallback_share_url || "";

  const copyLink = async () => {
    if (!fallback) return;
    try { await navigator.clipboard.writeText(fallback); }
    catch (err) { console.warn("[BeneficiaryInvites] copy failed:", err); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${delivered ? "" : ""}`}
           style={{background: delivered ? "#EEF3EA" : "#FDF8F0", color: delivered ? "#4B6A4F" : "#8A6B4F"}}>
        {delivered ? <MailCheck size={16} className="mt-0.5 shrink-0"/> : <AlertCircle size={16} className="mt-0.5 shrink-0"/>}
        <div>
          {delivered ? (
            <>Email sent to <strong>{result.invite?.beneficiary_email}</strong>. They'll receive a link along with instructions.</>
          ) : (
            <>Email delivery failed ({result.email_delivery?.reason || "unknown"}). Copy the link below and share it manually.</>
          )}
        </div>
      </div>

      {fallback && (
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{color:"var(--text-secondary)"}}>
            Direct link (share only with the beneficiary)
          </label>
          <div className="flex items-center gap-2">
            <input readOnly value={fallback}
                   className="flex-1 rounded-lg border px-3 py-2 text-xs font-mono"
                   style={{borderColor:"var(--border)",background:"var(--bg-input)",color:"var(--text-primary)"}}
                   data-testid="beneficiary-invite-fallback-url" />
            <button onClick={copyLink}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border"
                    style={{borderColor:"var(--border)",color:"var(--text-primary)"}}>
              {copied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy</>}
            </button>
          </div>
          <p className="text-[11px] mt-1.5" style={{color:"var(--text-secondary)"}}>
            The beneficiary will still need to enter their date of birth and a 6-digit code we email them.
          </p>
        </div>
      )}

      <div className="text-right">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                style={{background:"#C85A32"}} data-testid="beneficiary-invite-done">Done</button>
      </div>
    </div>
  );
}

function formatShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return "—"; }
}

/**
 * Surfaces the beneficiary's free-text `notes_to_petitioner` field so the
 * case owner sees any requests to add new family members (which affect the
 * platform fee) after the beneficiary submits their review.
 */
function BeneficiaryNoteToPetitionerBanner({ caseData }) {
  const note = (caseData?.data && caseData.data.beneficiary_notes_to_petitioner) || "";
  if (!note.trim()) return null;
  return (
    <div className="mt-3 rounded-lg border-l-4 p-3 text-sm flex items-start gap-2"
         style={{background:"#FDF8F0", borderColor:"#C85A32", color:"#2F2A24"}}
         data-testid="beneficiary-note-banner">
      <Info size={16} className="mt-0.5 shrink-0" color="#C85A32" />
      <div>
        <div className="text-[10px] uppercase tracking-widest mb-1" style={{color:"#8A6B4F"}}>
          Note from the beneficiary
        </div>
        <div className="italic mb-2">&ldquo;{note}&rdquo;</div>
        <div className="text-xs flex items-start gap-1" style={{color:"#8A6B4F"}}>
          <DollarSign size={12} className="mt-0.5" />
          <span>If they mention new family members, add them to the case first — the platform fee will update automatically before payment.</span>
        </div>
      </div>
    </div>
  );
}
